<template>
    <dropdown
        :position="position"
        :icon="icon ? icon : ''"
        :class="classes"
        class="canned-messages canned-messages--conversation"
        :tooltip="`<span class='canned-messages__label-tooltip'>Saved Replies
                <svg width='18' height='18' viewBox='0 0 18 18' fill='none' xmlns='http://www.w3.org/2000/svg'>
                    <path d='M11 5L7 13' stroke='#b5b5b5' />
                    <rect x='0.5' y='0.5' width='17' height='17' rx='2.5' stroke='#b5b5b5' />
                </svg>
            </span>`"
        :tooltip-class="'canned-messages__tooltip-icon'"
        keep-open-after-select
        @icon-click-after="iconClickAfter"
    >
        <div
            class="label"
            :class="{
                loadingState: busy,
            }"
        >
            <span class="canned-messages__label">
                Saved Replies
                <svg
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path d="M11 5L7 13" stroke="#4B4B4B" />
                    <rect x="0.5" y="0.5" width="17" height="17" rx="2.5" stroke="#4B4B4B" />
                </svg>
            </span>
            <router-link
                v-if="$route.path.includes('widget')"
                target="_blank"
                to="/settings/personal/settings/canned-messages"
                class="button is-small is-primary"
            >
                Create New
            </router-link>
            <router-link
                v-else
                to="/settings/personal/settings/canned-messages"
                class="button is-small is-primary"
            >
                Create New
            </router-link>
        </div>
        <div
            v-if="searchOnTop"
            class="dropdown-item is-no-hover canned-messages-search"
            :class="{
                loadingState: busy,
            }"
        >
            <input v-model="query" type="text" class="input" placeholder="Search..." />
            <a v-if="query" class="canned-messages-clear" @click.stop="clear">
                <fa-icon icon="times" />
            </a>
        </div>

        <div
            class="canned-messages-container"
            :class="{
                loadingState: busy,
            }"
        >
            <div
                id="canned-messages-list"
                ref="cannedMsgList"
                class="canned-messages-list"
                @scroll="scrollHandler"
            >
                <a
                    v-for="(canned, index) in messagesCanned"
                    :key="canned.id"
                    :data-id="index"
                    class="dropdown-item"
                    :class="{
                        [`dropdown-item-canned-${canned.id}`]: true,
                        'active-item': currentItem === index,
                    }"
                    @click="applyCannedMessage(canned, index)"
                >
                    <div class="canned-messages-box">
                        <strong>{{ canned.title }}</strong>
                        <div v-if="canned.substringMessage" class="canned-messages-item">
                            {{ canned.substringMessage }}
                        </div>
                        <div v-else-if="canned.media_url">
                            <em>Image</em>
                        </div>
                    </div>
                </a>
            </div>
        </div>

        <div
            v-if="query !== '' && hasNoResults"
            class="dropdown-item is-no-hover is-no-results"
            :class="{
                loadingState: busy,
            }"
        >
            <div class="help has-text-danger">No Results</div>
        </div>
        <div v-if="busy" class="canned-messages-loader-wrapper" :class="{ hasNoResults }">
            <basic-loader center aria-label="CannedMessages" />
        </div>
        <div
            v-if="!searchOnTop"
            class="dropdown-item is-no-hover canned-messages-search"
            :class="{
                loadingState: busy,
            }"
        >
            <input
                ref="search"
                v-model="query"
                type="text"
                class="input canned_messages_input"
                placeholder="Search..."
                @keyup.prevent="scrollNextItem"
            />
            <a v-if="query" class="canned-messages-clear" @click.stop="clear">
                <fa-icon icon="times" />
            </a>
        </div>
    </dropdown>
</template>

<script>
import { mapState } from 'pinia';
import stores from 'store/modules';
import { debounce } from 'lodash';

export default {
    emits: ['selected'],

    props: {
        contact: { type: Object },
        position: { type: String, default: 'left' },
        icon: { type: [String, Boolean], default: false },
        integration: { type: Object },
    },

    data: () => ({
        busy: false,
        query: '',
        currentItem: 0,
    }),

    computed: {
        searchOnTop() {
            return this.position.indexOf('bottom') !== -1;
        },

        classes() {
            return {
                'is-busy': this.busy,
                'has-results': !this.busy && !this.messagesCanned.length,
                'dropdown-body__open': !this.busy,
            };
        },

        hasMoreResults() {
            return this.paginationCanned.hasNextPage;
        },

        hasNoResults() {
            return !this.messagesCanned.length;
        },

        ...mapState(stores.Messages, ['messagesCanned', 'paginationCanned']),
        ...mapState(stores.Dropdown, ['dropdown']),
    },

    watch: {
        query(val) {
            if (val) {
                this.searchDebounce();
                this.currentItem = 0;
            } else {
                this.clear();
            }
        },

        'dropdown.active'(val) {
            if (val) {
                this.currentItem = 0;
            }
        },
    },

    methods: {
        iconClickAfter(e) {
            if (e) e.stopPropagation();

            // case when user select item, and we don't need to reload data because data has been already loaded
            if (!this.currentItem) {
                this.$store.dispatch('resetCannedMessages');
                this.$store.dispatch('resetCannedMessagesPagination');
                this.load();
            }
        },

        async load(page = 1) {
            if (!this.hasMoreResults) return;
            if (this.busy) return;

            const defaultLimit = 1000;
            let params = {};

            if (this.contact) {
                params = {
                    length: defaultLimit,
                    contact_id: this.contact.id,
                    integration_id: this.contact.integration.id,
                    ...(this.query !== '' ? { search: this.query } : {}),
                    page: page,
                };
            } else {
                if (this.integration) {
                    params = {
                        withIntegration: this.integration.key,
                        length: defaultLimit,
                        ...(this.query !== '' ? { search: this.query } : {}),
                        page: page,
                    };
                } else {
                    params = {
                        isNotIntegration: true,
                        length: defaultLimit,
                        ...(this.query !== '' ? { search: this.query } : {}),
                        page: page,
                    };
                }
            }

            this.busy = true;

            try {
                await this.$store.dispatch('getCannedMessages', params);
            } catch (e) {
                this.$log.error(e, this.$options.__file, 'load');
            } finally {
                this.busy = false;
            }
        },

        loadMore() {
            this.load(this.paginationCanned.currentPage + 1);
        },

        search() {
            this.$store.dispatch('resetCannedMessagesPagination', true);

            this.load();
        },

        clear() {
            this.query = '';
            this.$store.dispatch('resetCannedMessagesPagination', true);

            this.load();
        },

        applyCannedMessage(result, index) {
            this.query = '';
            this.$emit('selected', result);
            // used as indicator to keep dropdown active and to not reload data
            this.currentItem = index;
        },

        scrollHandler(event) {
            this.scrollDebounce(event);
        },

        scrollDebounce: debounce(function (event) {
            const target = event.target;
            if (!target) {
                return;
            }
            const scrollHeight = target.scrollHeight;
            const currentPositionX = target.scrollTop + target.clientHeight + 150;
            if (currentPositionX >= scrollHeight) {
                this.loadMore();
            }
        }, 300),

        searchFocus() {
            if (this.$refs.search) {
                this.$refs.search.focus();
            }
        },

        scrollNextItem(e) {
            const scrollSection = this.$refs.cannedMsgList;

            if (!scrollSection) return;

            const activeItem = scrollSection.querySelector('.active-item');
            const activeItemDataId = activeItem && activeItem.getAttribute('data-id');

            if (e.keyCode === 13) {
                this.applyCannedMessage(this.messagesCanned[activeItemDataId]);
            } else if (e.keyCode === 40 && this.currentItem < this.messagesCanned.length - 1) {
                if (activeItem) {
                    scrollSection.scrollTop = activeItem.offsetTop;
                    this.currentItem++;
                }
            } else if (e.keyCode === 38 && this.currentItem > 0) {
                if (activeItem) {
                    scrollSection.scrollTop = scrollSection.scrollTop - activeItem.clientHeight;
                    this.currentItem--;
                }
            } else if (e.keyCode === 27 && this.dropdown.active) {
                this.$store.dispatch('dropdownClose');
            }
        },
    },

    mounted() {
        this.searchDebounce = debounce(this.search, 300);
        this.searchFocusDebounce = debounce(this.searchFocus, 300);
    },

    updated() {
        this.searchFocusDebounce();
    },
};
</script>

<style scoped>
.active-item {
    background-color: whitesmoke;
}
</style>
